import { useState, useEffect } from "react";
import api from "../lib/api";
import { Button } from "../components/ui/button";
import { Input } from "../components/ui/input";
import { toast } from "sonner";
import {
  CreditCard, Mail, Phone, Star, CalendarDays, Calculator, Zap, Camera,
  Megaphone, RefreshCw, Check, X, Settings2, Plug, Search, ExternalLink,
  ShieldCheck, AlertCircle, Square
} from "lucide-react";

const categoryLabels = {
  payments: { label: "Payments", icon: CreditCard, color: "#2D5936" },
  communications: { label: "Communications", icon: Mail, color: "#004085" },
  marketing: { label: "Marketing", icon: Megaphone, color: "#805B10" },
  reviews: { label: "Reviews & Reputation", icon: Star, color: "#F59E0B" },
  calendar: { label: "Calendar & Scheduling", icon: CalendarDays, color: "#8B5CF6" },
  accounting: { label: "Accounting", icon: Calculator, color: "#1A1A1A" },
  developer: { label: "Developer & Automation", icon: Zap, color: "#EC4899" },
};

const iconMap = {
  "credit-card": CreditCard, square: Square, mail: Mail, phone: Phone,
  star: Star, calendar: CalendarDays, calculator: Calculator, zap: Zap,
  camera: Camera, megaphone: Megaphone,
};

export default function IntegrationsPage() {
  const [integrations, setIntegrations] = useState([]);
  const [loading, setLoading] = useState(true);
  const [search, setSearch] = useState("");
  const [catFilter, setCatFilter] = useState("all");
  const [setupId, setSetupId] = useState(null);
  const [setupConfig, setSetupConfig] = useState({});
  const [saving, setSaving] = useState(false);

  const fetchAll = async () => {
    setLoading(true);
    try {
      const res = await api.get("/integrations");
      setIntegrations(res.data || []);
    } catch { toast.error("Failed to load integrations"); }
    setLoading(false);
  };

  useEffect(() => { fetchAll(); }, []);

  const openSetup = (intg) => {
    setSetupId(intg.integration_id);
    setSetupConfig(intg.config || {});
  };

  const saveSetup = async () => {
    setSaving(true);
    try {
      await api.put(`/integrations/${setupId}`, { config: setupConfig });
      toast.success("Integration saved");
      setSetupId(null);
      fetchAll();
    } catch { toast.error("Failed to save"); }
    setSaving(false);
  };

  const testConnection = async (id) => {
    try {
      const res = await api.post(`/integrations/${id}/test`);
      if (res.data.success) toast.success(res.data.message);
      else toast.error(res.data.message);
    } catch { toast.error("Test failed"); }
  };

  const disconnect = async (id) => {
    try {
      await api.post(`/integrations/${id}/disconnect`);
      toast.success("Disconnected");
      fetchAll();
    } catch { toast.error("Failed"); }
  };

  const setupIntg = integrations.find(i => i.integration_id === setupId);
  const categories = [...new Set(integrations.map(i => i.category))];
  const filtered = integrations.filter(i => {
    if (catFilter !== "all" && i.category !== catFilter) return false;
    if (search && !i.name.toLowerCase().includes(search.toLowerCase())) return false;
    return true;
  });
  const connectedCount = integrations.filter(i => i.status === "connected").length;

  if (loading) return <div className="p-8 flex items-center justify-center min-h-[400px]"><RefreshCw className="w-5 h-5 animate-spin text-rose-400" /></div>;

  return (
    <div data-testid="integrations-page" className="p-6 lg:p-8 max-w-[1400px] mx-auto space-y-6">
      <div className="flex flex-col sm:flex-row sm:items-center sm:justify-between gap-4">
        <div>
          <h1 className="font-['Outfit'] text-2xl font-semibold text-slate-900 tracking-tight">Integrations</h1>
          <p className="text-sm text-slate-400 mt-1">{connectedCount} of {integrations.length} connected</p>
        </div>
      </div>

      {/* Search + Filter */}
      <div className="flex flex-col sm:flex-row gap-3">
        <div className="relative flex-1 max-w-sm">
          <Search className="w-4 h-4 absolute left-3 top-1/2 -translate-y-1/2 text-slate-400" />
          <Input value={search} onChange={e => setSearch(e.target.value)} placeholder="Search integrations..." className="pl-9 border-slate-200" data-testid="search-integrations" />
        </div>
        <div className="flex gap-2 flex-wrap">
          <button onClick={() => setCatFilter("all")} className={`px-3 py-1.5 rounded-lg text-xs font-medium transition-all ${catFilter === "all" ? "bg-slate-900 text-white" : "bg-white border border-slate-200 text-slate-600"}`}>All</button>
          {categories.map(cat => {
            const cl = categoryLabels[cat] || {};
            return <button key={cat} onClick={() => setCatFilter(cat)} className={`px-3 py-1.5 rounded-lg text-xs font-medium transition-all ${catFilter === cat ? "bg-slate-900 text-white" : "bg-white border border-slate-200 text-slate-600"}`}>{cl.label || cat}</button>;
          })}
        </div>
      </div>

      {/* Integration Cards by Category */}
      {(catFilter === "all" ? categories : [catFilter]).map(cat => {
        const catIntgs = filtered.filter(i => i.category === cat);
        if (catIntgs.length === 0) return null;
        const cl = categoryLabels[cat] || { label: cat, color: "#5E5E5E" };
        const CatIcon = cl.icon || Plug;
        return (
          <div key={cat}>
            <div className="flex items-center gap-2 mb-3">
              <CatIcon className="w-4 h-4" style={{ color: cl.color }} />
              <h2 className="font-['Outfit'] font-semibold text-sm text-slate-900 uppercase tracking-wider">{cl.label}</h2>
            </div>
            <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 gap-4">
              {catIntgs.map(intg => {
                const Icon = iconMap[intg.icon] || Plug;
                const connected = intg.status === "connected";
                return (
                  <div key={intg.integration_id} data-testid={`intg-card-${intg.integration_id}`} className={`bg-white border rounded-2xl p-5 transition-all hover:shadow-sm ${connected ? "border-[#C3E6CB]" : "border-slate-200"}`}>
                    <div className="flex items-start justify-between mb-3">
                      <div className="flex items-center gap-3">
                        <div className={`w-10 h-10 rounded-lg flex items-center justify-center ${connected ? "bg-[#D4EDDA]" : "bg-[#FAFAFA]"}`}>
                          <Icon className={`w-5 h-5 ${connected ? "text-[#155724]" : "text-slate-600"}`} />
                        </div>
                        <div>
                          <h3 className="font-['Outfit'] font-semibold text-slate-900 text-sm">{intg.name}</h3>
                          <span className={`text-[10px] px-1.5 py-0.5 rounded-full font-medium ${connected ? "bg-[#D4EDDA] text-[#155724]" : "bg-slate-50 text-slate-400"}`}>
                            {connected ? "Connected" : "Not Connected"}
                          </span>
                        </div>
                      </div>
                    </div>
                    <p className="text-xs text-slate-400 mb-4 leading-relaxed">{intg.description}</p>
                    <div className="flex gap-2">
                      <Button size="sm" variant={connected ? "outline" : "default"} onClick={() => openSetup(intg)} className={connected ? "border-slate-200 text-slate-600" : "bg-slate-900 text-white"} data-testid={`setup-${intg.integration_id}`}>
                        <Settings2 className="w-3 h-3 mr-1" /> {connected ? "Configure" : "Set Up"}
                      </Button>
                      {connected && (
                        <>
                          <Button size="sm" variant="outline" onClick={() => testConnection(intg.integration_id)} className="border-[#C3E6CB] text-[#155724]">
                            <ShieldCheck className="w-3 h-3 mr-1" /> Test
                          </Button>
                          <Button size="sm" variant="ghost" onClick={() => disconnect(intg.integration_id)} className="text-rose-600">
                            <X className="w-3 h-3" />
                          </Button>
                        </>
                      )}
                    </div>
                  </div>
                );
              })}
            </div>
          </div>
        );
      })}

      {/* Setup Modal */}
      {setupIntg && (
        <div className="fixed inset-0 bg-black/30 flex items-center justify-center z-50 p-4" onClick={() => setSetupId(null)}>
          <div data-testid="setup-modal" className="bg-white rounded-2xl p-6 max-w-lg w-full space-y-4 max-h-[80vh] overflow-y-auto" onClick={e => e.stopPropagation()}>
            <div className="flex items-center justify-between">
              <div className="flex items-center gap-3">
                {(() => { const Icon = iconMap[setupIntg.icon] || Plug; return <div className="w-10 h-10 rounded-lg bg-[#FAFAFA] flex items-center justify-center"><Icon className="w-5 h-5 text-slate-600" /></div>; })()}
                <div>
                  <h3 className="font-['Outfit'] font-semibold text-slate-900">{setupIntg.name} Setup</h3>
                  <p className="text-xs text-slate-400">{categoryLabels[setupIntg.category]?.label}</p>
                </div>
              </div>
              <Button variant="ghost" size="sm" onClick={() => setSetupId(null)}><X className="w-4 h-4" /></Button>
            </div>

            <p className="text-sm text-slate-600">{setupIntg.description}</p>

            <div className="space-y-3">
              {(setupIntg.fields || []).map(field => (
                <div key={field.key}>
                  <label className="text-xs text-slate-400 mb-1 block">{field.label}</label>
                  <Input
                    type={field.type === "password" ? "password" : "text"}
                    value={setupConfig[field.key] || ""}
                    onChange={e => setSetupConfig(c => ({ ...c, [field.key]: e.target.value }))}
                    placeholder={field.placeholder}
                    className="border-slate-200"
                    data-testid={`field-${field.key}`}
                  />
                </div>
              ))}
            </div>

            <div className="bg-[#FAFAFA] rounded-lg p-3 text-xs text-slate-400">
              <AlertCircle className="w-3 h-3 inline mr-1" />
              API keys are stored securely and never exposed in the UI after saving.
            </div>

            <div className="flex gap-2 pt-2">
              <Button onClick={saveSetup} disabled={saving} className="bg-slate-900 text-white" data-testid="save-integration-btn">
                {saving ? <RefreshCw className="w-4 h-4 animate-spin mr-1" /> : <Check className="w-4 h-4 mr-1" />}
                {saving ? "Saving..." : "Save & Connect"}
              </Button>
              <Button variant="outline" onClick={() => setSetupId(null)} className="border-slate-200">Cancel</Button>
            </div>
          </div>
        </div>
      )}
    </div>
  );
}
